import { useEffect, useState } from 'react';
import { Megaphone, Send, Users, Filter } from 'lucide-react';
import { crmApi } from '../crmApi';
import { UserSession } from '../../../types';

interface Props { session?: UserSession | null; }

type Origem = 'leads' | 'contatos';

const DisparoWhatsapp = ({ session }: Props) => {
  const meuEmail = (session as any)?.usuario?.email || '';
  const [origem, setOrigem] = useState<Origem>('leads');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroTemperatura, setFiltroTemperatura] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [minhaCarteira, setMinhaCarteira] = useState(true);
  const [registros, setRegistros] = useState<any[]>([]);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [templates, setTemplates] = useState<any[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const [carregando, setCarregando] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      if (origem === 'leads') {
        const list = await crmApi.listarLeads(session);
        setRegistros(list);
      } else {
        const list = await crmApi.listarContatos(session);
        setRegistros(list);
      }
      setSelecionados(new Set());
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setCarregando(false); }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [origem]);
  useEffect(() => {
    crmApi.templates.listar(session).then(setTemplates).catch(() => {});
  }, [session]);

  const filtrados = registros.filter(r => {
    if (minhaCarteira && r.responsavelId !== meuEmail) return false;
    if (origem === 'leads') {
      if (filtroStatus && r.status !== filtroStatus) return false;
      if (filtroTemperatura && r.temperatura !== filtroTemperatura) return false;
    } else {
      if (filtroTipo && r.tipo !== filtroTipo) return false;
    }
    return !!r.telefone;
  });

  const toggleTodos = () => {
    if (selecionados.size === filtrados.length) setSelecionados(new Set());
    else setSelecionados(new Set(filtrados.map(r => r.id)));
  };

  const toggle = (id: string) => {
    const s = new Set(selecionados);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelecionados(s);
  };

  const enviar = async () => {
    if (selecionados.size === 0) { alert('Selecione ao menos um destinatário.'); return; }
    if (!templateId && !mensagem.trim()) { alert('Escolha um template ou escreva a mensagem.'); return; }
    if (!confirm(`Enviar para ${selecionados.size} destinatário(s)?`)) return;
    setEnviando(true);
    setResultado(null);
    try {
      const destinatarios = filtrados
        .filter(r => selecionados.has(r.id))
        .map(r => ({
          numero: r.telefone, nome: r.nome,
          variaveis: { nome: r.nome, empresa: r.empresa || '' },
          ...(origem === 'leads' ? { leadId: r.id } : { contatoId: r.id }),
        }));
      const r = await crmApi.whatsapp.enviarLote(session, {
        destinatarios,
        mensagem: mensagem || undefined,
        templateId: templateId || undefined,
      });
      setResultado(r);
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setEnviando(false); }
  };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4" /> Filtrar destinatários
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <select value={origem} onChange={e => setOrigem(e.target.value as Origem)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white">
            <option value="leads">Leads</option>
            <option value="contatos">Contatos</option>
          </select>
          {origem === 'leads' ? (
            <>
              <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white">
                <option value="">Todos os status</option>
                <option value="novo">Novo</option>
                <option value="em_contato">Em contato</option>
                <option value="qualificado">Qualificado</option>
              </select>
              <select value={filtroTemperatura} onChange={e => setFiltroTemperatura(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white">
                <option value="">Todas as temperaturas</option>
                <option value="frio">Frio</option>
                <option value="morno">Morno</option>
                <option value="quente">Quente</option>
              </select>
            </>
          ) : (
            <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white">
              <option value="">Todos os tipos</option>
              <option value="prospect">Prospect</option>
              <option value="cliente">Cliente</option>
              <option value="parceiro">Parceiro</option>
              <option value="fornecedor">Fornecedor</option>
            </select>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={minhaCarteira} onChange={e => setMinhaCarteira(e.target.checked)} />
            Apenas minha carteira
          </label>
        </div>
      </div>

      {/* Lista + mensagem */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl flex flex-col">
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <Users className="h-4 w-4" /> {filtrados.length} com telefone · {selecionados.size} selecionado{selecionados.size !== 1 ? 's' : ''}
            </h3>
            <button onClick={toggleTodos} className="text-xs text-blue-600 hover:underline">
              {selecionados.size === filtrados.length && filtrados.length > 0 ? 'Limpar' : 'Selecionar todos'}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[400px]">
            {carregando ? <p className="p-4 text-xs text-gray-400 text-center">Carregando…</p>
            : filtrados.length === 0 ? <p className="p-4 text-xs text-gray-400 text-center">Nenhum destinatário encontrado.</p>
            : filtrados.map(r => (
              <label key={r.id} className="flex items-center gap-2 px-3 py-2 border-b border-gray-50 hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={selecionados.has(r.id)} onChange={() => toggle(r.id)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{r.nome}</p>
                  <p className="text-xs text-gray-500 truncate">{r.empresa || '—'} · {r.telefone}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <div>
            <label className="text-xs text-gray-600">Template</label>
            <select value={templateId} onChange={e => setTemplateId(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white">
              <option value="">— Sem template —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">Use {'{{nome}}'} e {'{{empresa}}'} no template para personalização automática.</p>
          </div>
          <div>
            <label className="text-xs text-gray-600">Ou mensagem direta</label>
            <textarea value={mensagem} onChange={e => setMensagem(e.target.value)}
              rows={6} placeholder="Olá {{nome}}, ..."
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md resize-none" />
          </div>
          <button onClick={enviar} disabled={enviando || selecionados.size === 0}
            className="w-full px-3 py-2.5 text-sm font-semibold bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-40 flex items-center justify-center gap-2">
            <Send className="h-4 w-4" />
            {enviando ? 'Enviando...' : `Disparar para ${selecionados.size}`}
          </button>

          {resultado && (
            <div className="text-xs p-3 rounded-md border border-gray-200 bg-gray-50">
              <p className="font-semibold text-gray-800 mb-1 flex items-center gap-1"><Megaphone className="h-3 w-3" /> Resultado</p>
              <p>✅ Enviados: <b>{resultado.enviados}</b></p>
              <p>❌ Falhas: <b>{resultado.falhas}</b></p>
              <p>📊 Total: <b>{resultado.total}</b></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DisparoWhatsapp;
